import { SWMessageType } from '@/enums/SWMessageType.enum';
import {
  imageUploadCompleted,
  imageUploadStatus,
  inspectionDataUploadStatus,
  inspectionUpdating,
  inspectionUploadStarted,
  updateMessage,
  updateProgress,
} from '@/store/slices/uploadProgressSlice';
import store from '@/store/store';
import { logout } from '@/store/slices/authSlice';
import { systemConfig } from '@/config/systemConfig';
import { ActivityUpdateType } from '@/enums/ActivityType.enum';
import { SWState } from '@/enums/SWState.enum';
import { SWMessage } from '@/serviceworker/types';
import { SyncNotificationPayload } from '@/types/SyncNotificationPayload';
import { logDebug, logError } from '@/utils/logger';
import {
  logFactoryVerificationDebug,
  logFactoryVerificationError,
} from '@/utils/factoryVerificationLogger.util';
import { AppConfig } from '@/types/AppConfig.type';

const debugLog = (message: string, data: any) => {
  logDebug(
    systemConfig.apiUrl,
    store.getState().auth.token || '',
    store.getState().auth.user,
    message,
    { ...data, version: systemConfig.version }
  );
};

const errorLog = (message: string, data: any) => {
  logError(
    systemConfig.apiUrl,
    store.getState().auth.token || '',
    store.getState().auth.user,
    message,
    { ...data, version: systemConfig.version }
  );
};

// Utility function to ensure service worker is properly registered
const ensureServiceWorkerReady = async (): Promise<boolean> => {
  if (!('serviceWorker' in navigator)) {
    errorLog('[ensureServiceWorkerReady] Service Worker not supported', {});
    return false;
  }

  try {
    // Wait for service worker to be ready
    const registration = await navigator.serviceWorker.ready;

    // Check if we have an active service worker
    if (registration.active) {
      debugLog('[ensureServiceWorkerReady] Service Worker is active', {});
      return true;
    }

    // If no active service worker, wait a bit and check again
    debugLog('[ensureServiceWorkerReady] Waiting for service worker to become active...', {});
    await new Promise((resolve) => setTimeout(resolve, 2000));

    if (registration.active) {
      debugLog('[ensureServiceWorkerReady] Service Worker is now active after waiting', {});
      return true;
    }

    errorLog('[ensureServiceWorkerReady] Service Worker failed to become active', {});
    return false;
  } catch (error) {
    errorLog('[ensureServiceWorkerReady] Error ensuring service worker ready:', error);
    return false;
  }
};

const startUpload = (inspectionIds: string[]) => {
  // Check if service worker is supported
  if (!('serviceWorker' in navigator)) {
    errorLog('[mediator ----startUpload] Service Worker not supported in this browser', {});
    return;
  }

  // Check if service worker controller is available
  if (!navigator.serviceWorker.controller) {
    debugLog(
      '[mediator ----startUpload] Service Worker controller not available. Attempting to register...',
      {}
    );

    // Try to ensure service worker is ready
    ensureServiceWorkerReady()
      .then((isReady) => {
        if (isReady) {
          debugLog(
            '[mediator ----startUpload] Service Worker is now ready, retrying upload...',
            {}
          );
          // Retry the upload after a short delay
          setTimeout(() => startUpload(inspectionIds), 1000);
        } else {
          errorLog('[mediator ----startUpload] Service Worker registration failed', {});
        }
      })
      .catch((error) => {
        errorLog('[mediator ----startUpload] Service Worker registration error:', error);
      });

    return;
  }

  // Service worker is available, proceed with upload
  const message = {
    type: SWMessageType.UPLOAD_DATA,
    payload: {
      inspectionIds: inspectionIds,
      config: store.getState().appConfig,
      token: store.getState().auth.token,
      apiUrl: systemConfig.apiUrl,
    },
  };
  debugLog('[mediator ----startUpload] sending message to service worker', message);

  try {
    navigator.serviceWorker.controller.postMessage(message);
  } catch (error) {
    errorLog('[mediator ----startUpload] Failed to send message to service worker:', error);
  }
};

const sendUserDetailsToSW = () => {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: SWMessageType.USER_IDENTITY,
      payload: store.getState().auth.user,
    });
  } else {
    errorLog('[mediator ----sendUserDetailsToSW] no service worker found', {});
  }
};

const startLocationEvidenceUpload = (inspectionId: string, locationCode?: string) => {
  if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
    logFactoryVerificationError('upload.swUnavailable', { inspectionId, locationCode });
    return;
  }
  const message = {
    type: SWMessageType.UPLOAD_LOCATION_EVIDENCE,
    payload: {
      inspectionId,
      locationCode,
      token: store.getState().auth.token,
      apiUrl: systemConfig.apiUrl,
      config: store.getState().appConfig,
    },
  };
  try {
    navigator.serviceWorker.controller.postMessage(message);
    logFactoryVerificationDebug('upload.messageSent', { inspectionId, locationCode });
  } catch (error) {
    errorLog('[mediator ----startLocationEvidenceUpload] Failed to send message:', error);
    logFactoryVerificationError('upload.messageFailed', { inspectionId, locationCode, error });
  }
};

const listenForMessages = () => {
  if ('serviceWorker' in navigator) {
    const messageHandler = (event: MessageEvent) => {
      debugLog('[mediator ----listenForMessages] Message from Service Worker:', event.data);
      const swMsg = event.data as SWMessage;
      // Handle the message based on type
      switch (swMsg.type) {
        case SWMessageType.UPLOAD_STATUS:
          const payload = swMsg.payload as SyncNotificationPayload;
          switch (payload.status) {
            case SWState.PROCESSING:
              switch (payload.activity?.type) {
                case ActivityUpdateType.IMAGE:
                  store.dispatch(imageUploadStatus(payload.activity));
                  break;
                case ActivityUpdateType.IMAGE_UPLOAD_COMPLETED:
                  store.dispatch(imageUploadCompleted(payload.activity));
                  break;
                case ActivityUpdateType.INSPECTION:
                  store.dispatch(inspectionDataUploadStatus(payload.activity));
                  break;
                case ActivityUpdateType.INSPECTION_STARTED:
                  store.dispatch(inspectionUploadStarted(payload.activity));
                  break;
                case ActivityUpdateType.INSPECTION_UPDATING:
                  store.dispatch(inspectionUpdating(payload.activity));
                  break;
              }
              break;
            case SWState.COMPLETED:
            case SWState.STARTING:
            case SWState.IDLE:
              store.dispatch(updateProgress(payload));
              break;
          }
          break;
        case SWMessageType.AUTH_ERROR:
          if (swMsg.payload?.status === 401) {
            store.dispatch(logout());
          } else {
            store.dispatch(updateMessage('Not authorized.'));
            errorLog('Auth error:', swMsg.payload?.status); // ToDo: Forbid access to the app
          }
          break;
        default:
          // store.dispatch(updateMessage(swMsg?.payload?.message ?? ""));
          debugLog('[mediator ----listenForMessages] unknown message:', swMsg);
          break;
      }
    };
    navigator.serviceWorker.addEventListener('message', messageHandler);

    // Return cleanup function
    return () => {
      console.log('removing message handler');
      navigator.serviceWorker.removeEventListener('message', messageHandler);
      console.log('Event handlers removed');
    };
  } else {
    errorLog('[mediator ----listenForMessages] no service worker found', {});
  }
};
export const sendConfigToSW = (config: AppConfig) => {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: SWMessageType.CONFIG,
      config,
      version: systemConfig.version,
    });
  }
};
export { startUpload, startLocationEvidenceUpload, listenForMessages, sendUserDetailsToSW };
